/**
 * Schema Discovery Component
 *
 * NOTE: The analytics-backend (Cube.js integration) has been removed.
 * This component is preserved as a stub for backwards compatibility.
 * The type exports are maintained for dependent components.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle } from 'lucide-react';
import { DataSource } from './DataSourceConnector';

export interface SchemaField {
  name: string;
  type: string;
  description?: string;
  nullable: boolean;
  isPrimary: boolean;
  isForeign: boolean;
  constraints?: string[];
  suggestedBusinessName?: string;
  aiConfidence?: number;
  businessContext?: {
    category: string;
    importance: 'high' | 'medium' | 'low';
    suggestions: string[];
  };
}

export interface SchemaTable {
  name: string;
  description?: string;
  fields: SchemaField[];
  recordCount?: number;
  relationships?: Array<{
    table: string;
    field: string;
    foreignTable: string;
    foreignField: string;
    type: 'one-to-one' | 'one-to-many' | 'many-to-many';
  }>;
  suggestedBusinessName?: string;
  aiConfidence?: number;
}

export interface DiscoveredSchema {
  id: string;
  dataSourceId: string;
  tables: SchemaTable[];
  relationships: Array<{
    fromTable: string;
    fromField: string;
    toTable: string;
    toField: string;
    type: string;
  }>;
  metadata: {
    discoveryTimestamp: Date;
    aiModel: string;
    overallConfidence: number;
    industryContext?: string;
    suggestions: string[];
  };
}

interface SchemaDiscoveryProps {
  dataSource: DataSource;
  onSchemaDiscovered?: (schema: DiscoveredSchema) => void;
  onError?: (error: Error) => void;
  industryContext?: string;
  aiEnhancement?: boolean;
}

export const SchemaDiscovery: React.FC<SchemaDiscoveryProps> = () => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          Feature Unavailable
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Schema discovery is temporarily unavailable. The analytics backend
          has been removed from this installation.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          For data queries and visualization, please use the Data page which
          connects to your configured data sources.
        </p>
      </CardContent>
    </Card>
  );
};

export default SchemaDiscovery;
